import { inject, Injectable } from '@angular/core';
import { StorageService } from '@movilidad/app-core';

import { CartItem, RecentTrip, TripDirection } from './booking.model';

const MAX_PER_DIRECTION = 5;

function toRecent(item: CartItem): RecentTrip {
  return {
    stopId: item.stopId,
    stopName: item.direction === 'go' ? item.origin : item.destination,
    routeId: item.routeId,
    routeName: item.routeName,
    radialId: item.radialId,
    direction: item.direction,
    origin: item.origin,
    destination: item.destination,
  };
}

function isSamePair(a: RecentTrip, b: RecentTrip): boolean {
  return a.stopId === b.stopId && a.direction === b.direction;
}

/**
 * Los últimos pares origen–destino que compró cada usuario, sólo en el dispositivo (F12). Una parada es de una
 * sola ruta y el campus es el otro extremo, así que parada y sentido identifican el par.
 */
@Injectable({ providedIn: 'root' })
export class RecentTrips {
  private readonly storage = inject(StorageService);

  private key(userId: number | string): string {
    return `recent_trips_${userId}`;
  }

  private async stored(userId: number | string): Promise<RecentTrip[]> {
    const trips = await this.storage.get<RecentTrip[]>(this.key(userId));
    return Array.isArray(trips) ? trips : [];
  }

  async list(userId: number | string, direction: TripDirection): Promise<RecentTrip[]> {
    return (await this.stored(userId)).filter((trip) => trip.direction === direction);
  }

  /** Lo último del carrito queda primero. El tope es por sentido: muchas idas no deben borrar los regresos. */
  async remember(userId: number | string, items: readonly CartItem[]): Promise<void> {
    const merged = [...items.map(toRecent).reverse(), ...(await this.stored(userId))];
    const unique = merged.filter((trip, index) => merged.findIndex((other) => isSamePair(trip, other)) === index);
    const kept = (['go', 'return'] as const).flatMap((direction) =>
      unique.filter((trip) => trip.direction === direction).slice(0, MAX_PER_DIRECTION)
    );
    await this.storage.set(this.key(userId), kept);
  }
}
